import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, Spin, message } from 'antd';
import AdvDetailTab from './AdvDetailTab';
import AdvVideoTab from './AdvVideoTab';
import FloatingFooter from '../../components/utils/FloatingFooter';

import { useAuth } from '../../contexts/AppContext';

/**
 * TsAdvLanding manages advertisement info settings and video settings tabs.
 */
const TsAdvLanding = () => {
  const { token, selectedRole } = useAuth();
  const tsAdvId = selectedRole?.id || '';
  const API = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('info');    // active tab
  // Load advertisement information
  const loadAdv = useCallback(async () => {
    if (!tsAdvId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/ts/tsadv/${tsAdvId}/get-adv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setFormData(data);
    } catch (err) {
      message.error(' Failed to load advertisement info.');
    } finally {
      setLoading(false);
    }
  }, [API, token, tsAdvId]);

  useEffect(() => {
    loadAdv();
  }, [loadAdv]);

  useEffect(() => {
    if (activeTab === 'video' && tsAdvId) {
      const loadVideo = async () => {
        try {
          const res = await fetch(`${API}/ts/tsadv/${tsAdvId}/get-video`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res.status === 204) return; // no video configured
          const data = await res.json();
          if (res.ok && data?.video) {
            setFormData((prev) => ({
              ...prev,
              video: data.video.publicLink,
              videoId: data.video.id,
              videoName: data.video.name,
            }));
          }
        } catch (err) {
          console.error('[loadVideo Error]', err);
          message.error(' Failed to load video info');
        }
      };

      loadVideo();
    }
  }, [activeTab, tsAdvId, API, token]);


  // Update a top-level field
  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Keep specials as an array so load/edit/save all use the same shape.
  const handleNested = (parentKey, subKey, value) => {
    setFormData((prev) => {
      if (parentKey === 'specials') {
        const nextSpecials = Array.isArray(prev?.specials) ? [...prev.specials] : [];
        const index = Number(subKey);
        nextSpecials[index] = value;
        return {
          ...prev,
          specials: nextSpecials,
        };
      }

      return {
        ...prev,
        [parentKey]: {
          ...prev[parentKey],
          [subKey]: value,
        },
      };
    });
    setHasChanges(true);
  };

  const items = [
    {
      key: 'info',
      label: 'Info Settings',
      children: (
        <AdvDetailTab
          formData={formData}
          onChange={handleChange}
          onNested={handleNested}
          token={token}
        />
      ),
    },
    {
      key: 'video',
      label: 'Videos',
      children: (
        <AdvVideoTab
          formData={formData}
          onChange={handleChange}
          token={token}
        />
      ),
    },
  ];

  const handleSaveAll = async () => {
    if (!formData) return;

    try {
      if (activeTab === 'info') {
        // Save Info Settings
        const special = formData.specials?.[0];
        const isAllEmpty = !special?.startDatetime && !special?.endDatetime && !special?.image;
        const isAllFilled = !!special?.startDatetime && !!special?.endDatetime && !!special?.image;
        const hasPartialSpecial = !isAllEmpty && !isAllFilled;

        if (hasPartialSpecial) {
          message.warning('Specials is not fully filled and will be discarded.');
        }

        const transformedSpecials =
          special && !hasPartialSpecial
            ? [{
                startDatetime: new Date(special.startDatetime),
                endDatetime: new Date(special.endDatetime),
                image: special.image,
              }]
            : [{
                image: '',
                endDatetime: null,
                startDatetime: null,
              }];

        const cleanData = {
          ...formData,
          specials: transformedSpecials,
        };

        const res = await fetch(`${API}/ts/tsadv/${tsAdvId}/update-adv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(cleanData),
        });

        if (!res.ok) throw new Error('Save failed');
        message.success(' Info saved successfully');
      }

      if (activeTab === 'video') {
        const { video, videoName, videoId } = formData;
        // console.log('Saving video:', { video, videoName, videoId });
        if (!video || !videoName) {
          message.warning('Please upload a video and enter a name before saving.');
          return;
        }

        const payload = {
          ts_adv_id: tsAdvId,
          publicLink: video,
          name: videoName,
          ts_venue_ids: [],
        };

        const res = await fetch(
          `${API}/ts/tsadv/${videoId ? `${videoId}/update-video` : 'create-video'}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        const result = await res.json();
        if (!res.ok || !result?.video) throw new Error('Video save failed');

        message.success(videoId ? ' Video updated' : ' Video created');
        setFormData((prev) => ({ ...prev, videoId: result.video.id }));
      }

      setHasChanges(false);
    } catch (err) {
      console.error('[Save Error]', err);
      message.error(' Save failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-[8rem] overflow-hidden">
      <div className="max-w-7xl w-[92%] mx-auto pt-8 overflow-hidden">
        <h1 className="text-[1.8rem] font-semibold text-blue-600 mb-4">
          Advertisement Media Management
        </h1>

        <div className="bg-white shadow-lg rounded-2xl p-6">
          {!tsAdvId ? (
            <div className="text-center text-red-500 py-12">No tsAdvId found.</div>
          ) : loading || !formData ? (
            <div className="flex justify-center items-center py-16">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-950">
                <div className="text-sm font-semibold uppercase tracking-[0.08em] text-amber-700">Affected Venues</div>
                <div className="mt-2 text-sm text-amber-900">
                  Changes to Main Image and Specials will affect every venue listed below.
                </div>
                {Array.isArray(formData.venues) && formData.venues.length > 0 ? (
                  <>
                    <div className="mt-3 text-sm font-medium text-amber-950">
                      This advertisement currently affects {formData.venues.length} venue{formData.venues.length === 1 ? '' : 's'}.
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.venues.map((venue) => (
                        <span
                          key={venue.id}
                          className="inline-flex items-center rounded-full border border-amber-300 bg-white px-3 py-1 text-sm text-amber-900 shadow-sm"
                          title={venue.id}
                        >
                          {venue.name}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="mt-3 text-sm text-amber-800">
                    This advertisement is not currently bound to any venue.
                  </div>
                )}
              </div>

              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                type="card"
                className="rounded-xl bg-white"
                items={items}
              />
            </>
          )}
        </div>
      </div>

      {/* Shared footer save button */}
      {formData && (
        <FloatingFooter
          hasChanges={hasChanges}
          onSave={handleSaveAll}
          saveLabel="Save"
          multipleSave={false}
        />
      )}
    </div>
  );
};

export default TsAdvLanding;


